<template>
  <div
    :class="containerClasses"
    :data-testid="containerTestId"
    style="display: block"
  >
    <PeriodPickerHeader
      :header-text="visibleYear.label"
      :has-next-page="hasNextPage"
      :has-previous-page="hasPreviousPage"
      :new-layout="newLayout"
      @show-next-page="showNextPage"
      @show-previous-page="showPreviousPage"
      @header-click="$emit('year-click')"
    />
    <template
      v-for="(row, row_index) in periodRows"
      :key="`months-row-${row_index}`"
    >
      <div v-if="row.showYearSeparator" :class="yearSeparatorClasses" />
      <div :class="rowNames" :data-testid="rowTestId">
        <span
          v-for="(period, period_index) in row.periods"
          :key="`month-${period_index}`"
          :class="periodClasses(period)"
          :title="periodTooltip(period)"
          @click="handlePeriodClick(period)"
        >
          {{ period.label }}
          <i
            v-show="period.private && period.ledgerId"
            :class="privateIconClasses(period)"
          />
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import chunk from 'lodash/chunk'
import findLastIndex from 'lodash/findLastIndex'
import isEqual from 'lodash/isEqual'
import classNames from 'classnames'

import PeriodPickerHeader from './period-picker-header'
import PeriodPickerMixin from './period-picker-mixin'

const PeriodPickerMonths = {
  components: {
    PeriodPickerHeader,
  },
  mixins: [PeriodPickerMixin],
  props: {
    years: Array,
    ledgersPerYear: Number,
    currentLedgerId: {
      type: Number,
      default: null,
    },
    highlightedPeriod: Object,
    focusedYear: Object,
    newLayout: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['period-click', 'year-click'],
  data() {
    return {
      visibleYear: this.focusedYear,
    }
  },
  computed: {
    yearsWithPeriods() {
      const years = this.years

      const firstYear = years.findIndex((year) => this.hasPeriodsWithData(year))
      const lastYear = findLastIndex(years, (year) =>
        this.hasPeriodsWithData(year)
      )

      return years.slice(firstYear, lastYear + 1)
    },
    currentPeriods() {
      return this.visibleYear.periods
    },
    hasPreviousPage() {
      return this.visibleYearIndex > 0
    },
    hasNextPage() {
      return this.visibleYearIndex < this.yearsWithPeriods.length - 1
    },
    periodRows() {
      const rows = chunk(this.currentPeriods, 4)
      return rows.map((periods, index) => {
        if (!this.newLayout || index === 0) {
          return { periods, showYearSeparator: false }
        }
        const previousRow = rows[index - 1]
        const previousYear = this.calendarYearOf(
          previousRow[previousRow.length - 1]
        )
        const currentYear = this.calendarYearOf(periods[0])
        return {
          periods,
          showYearSeparator: previousYear !== currentYear,
        }
      })
    },
    yearSeparatorClasses() {
      return [
        'tw-border-t',
        'tw-border-solid',
        'tw-border-x-0',
        'tw-border-b-0',
        'tw-border-sf-slate-lighter-1',
        'tw-mx-1',
        'tw-my-2',
      ]
    },
    periodPickerType() {
      if (this.ledgersPerYear === 4) return 'quarters'
      if (this.ledgersPerYear === 1) return 'years'
      return 'months'
    },
    // New-layout specs target the picker via `data-testid` instead of the
    // legacy `.periodpicker-{type}` structural classes (which the new layout
    // no longer needs for styling).
    containerTestId() {
      return this.newLayout ? `periodpicker-${this.periodPickerType}` : null
    },
    rowTestId() {
      return this.newLayout ? `periodpicker-${this.periodPickerType}-row` : null
    },
    containerClasses() {
      if (this.newLayout) {
        return ['tw-block']
      }

      return classNames({
        'periodpicker-months': this.ledgersPerYear === 12,
        'periodpicker-quarters': this.ledgersPerYear === 4,
        'periodpicker-years': this.ledgersPerYear === 1,
      })
    },
    rowNames() {
      if (this.newLayout) {
        return classNames('tw-grid tw-w-full', {
          'tw-grid-cols-4': this.ledgersPerYear !== 4,
          'tw-grid-cols-2': this.ledgersPerYear === 4,
        })
      }

      return classNames({
        'periodpicker-months-row': this.ledgersPerYear === 12,
        'periodpicker-quarters-row': this.ledgersPerYear === 4,
        'periodpicker-years-row': this.ledgersPerYear === 1,
      })
    },
    nextYearClass() {
      return classNames({
        'not-visible': !this.hasNextPage,
      })
    },
    previousYearClass() {
      return classNames({
        'not-visible': !this.hasPreviousPage,
      })
    },
    visibleYearIndex() {
      return this.yearsWithPeriods.findIndex((year) =>
        isEqual(year, this.visibleYear)
      )
    },
  },
  watch: {
    focusedYear(newValue) {
      this.visibleYear = newValue
    },
  },
  methods: {
    periodClasses(period) {
      const isActive = isEqual(period, this.highlightedPeriod)
      const disabled = !period.ledgerId

      if (this.newLayout) {
        return [
          'tw-relative',
          'tw-h-10',
          'tw-leading-10',
          'tw-m-1',
          'tw-text-center',
          'tw-rounded-md',
          'tw-text-base',
          {
            'tw-bg-sf-primary tw-text-white tw-cursor-pointer': isActive,
            'tw-text-sf-slate-dark-1 hover:tw-bg-sf-slate-subtle-2 tw-cursor-pointer':
              !isActive && !disabled,
            'tw-text-sf-slate-dark-1/40 tw-cursor-default': disabled,
          },
        ]
      }

      return classNames({
        'period-picker-item': true,
        month: this.ledgersPerYear === 12,
        quarter: this.ledgersPerYear === 4,
        private: period.private && period.ledgerId,
        active: isActive,
        disabled,
      })
    },
    privateIconClasses(period) {
      if (this.newLayout) {
        const isActive = isEqual(period, this.highlightedPeriod)
        return [
          'fa',
          'fa-eye-slash',
          'tw-absolute',
          'tw-bottom-1',
          'tw-right-1',
          'tw-text-xs',
          isActive ? 'tw-text-white/70' : 'tw-text-sf-slate-light-1',
        ]
      }
      return ['fa', 'fa-eye-slash']
    },
    showPreviousPage() {
      const years = this.yearsWithPeriods
      this.visibleYear = years[this.visibleYearIndex - 1]
    },
    showNextPage() {
      const years = this.yearsWithPeriods
      this.visibleYear = years[this.visibleYearIndex + 1]
    },
    handlePeriodClick(period) {
      if (period.ledgerId) {
        this.$emit('period-click', period)
      }
    },
    calendarYearOf(period) {
      return period?.periodEndDate?.slice(0, 4)
    },
    periodTooltip(period) {
      const year = this.calendarYearOf(period)
      return year ? `${period.label} ${year}` : period.label
    },
  },
}

export default PeriodPickerMonths
</script>
